import type { ComponentProps } from 'react'

import { cx } from '@/lib/cx'

type IconProps = { className?: string }

function Icon({ className, children, ...rest }: ComponentProps<'svg'>) {
  return (
    <svg
      aria-hidden
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      className={cx('size-5 shrink-0', className)}
      {...rest}
    >
      {children}
    </svg>
  )
}

export function MoreIcon({ className }: IconProps) {
  return (
    <Icon className={className}>
      <circle cx="5" cy="12" r="1" />
      <circle cx="12" cy="12" r="1" />
      <circle cx="19" cy="12" r="1" />
    </Icon>
  )
}

export function UserIcon({ className }: IconProps) {
  return (
    <Icon className={className}>
      <circle cx="12" cy="8" r="4" />
      <path d="M4 20c1.5-3.5 4.5-5 8-5s6.5 1.5 8 5" />
    </Icon>
  )
}

export function ChevronLeftIcon({ className }: IconProps) {
  return (
    <Icon className={className}>
      <path d="m14 6-6 6 6 6" />
    </Icon>
  )
}

export function ChevronRightIcon({ className }: IconProps) {
  return (
    <Icon className={className}>
      <path d="m10 6 6 6-6 6" />
    </Icon>
  )
}

export function CheckIcon({ className }: IconProps) {
  return (
    <Icon className={className}>
      <path d="m5 12.5 4.5 4.5L19 7" />
    </Icon>
  )
}

export function StoreIcon({ className }: IconProps) {
  return (
    <Icon className={className}>
      <path d="M4 9 5.5 4h13L20 9" />
      <path d="M3 9h18v2.5a2.5 2.5 0 0 1-5 0 2.5 2.5 0 0 1-5 0 2.5 2.5 0 0 1-5 0 1.5 1.5 0 0 1-3 0V9z" />
      <path d="M5 14v7h14v-7" />
      <path d="M10 21v-4h4v4" />
    </Icon>
  )
}

export function KeyIcon({ className }: IconProps) {
  return (
    <Icon className={className}>
      <circle cx="8" cy="15" r="4.5" />
      <path d="M11.5 11.5 20 3" />
      <path d="M16.5 6.5 19 9" />
      <path d="M14 9l2.5 2.5" />
    </Icon>
  )
}

export function UsersIcon({ className }: IconProps) {
  return (
    <Icon className={className}>
      <circle cx="9" cy="8" r="3.5" />
      <path d="M2.5 20a6.5 6.5 0 0 1 13 0" />
      <path d="M16 4.6a3.5 3.5 0 0 1 0 6.8" />
      <path d="M17.5 13.6a6.5 6.5 0 0 1 4 6.4" />
    </Icon>
  )
}

export function SettingsIcon({ className }: IconProps) {
  return (
    <Icon className={className}>
      <path d="M4 7h10M18 7h2M4 17h2M10 17h10" />
      <circle cx="16" cy="7" r="2" />
      <circle cx="8" cy="17" r="2" />
    </Icon>
  )
}

export function BranchIcon({ className }: IconProps) {
  return (
    <Icon className={className}>
      <path d="M12 21s-6-5.2-6-10a6 6 0 0 1 12 0c0 4.8-6 10-6 10Z" />
      <circle cx="12" cy="11" r="2.5" />
    </Icon>
  )
}

export function MenuIcon({ className }: IconProps) {
  return (
    <Icon className={className}>
      <path d="M4 5h16M4 12h16M4 19h10" />
      <circle cx="18" cy="19" r="1.5" />
    </Icon>
  )
}

export function TableIcon({ className }: IconProps) {
  return (
    <Icon className={className}>
      <path d="M3 9h18M5 9v10M19 9v10M8 9V6h8v3" />
    </Icon>
  )
}

export function PrintIcon({ className }: IconProps) {
  return (
    <Icon className={className}>
      <path d="M7 9V4h10v5M7 17H4v-6a2 2 0 0 1 2-2h12a2 2 0 0 1 2 2v6h-3" />
      <rect x="7" y="14" width="10" height="6" />
    </Icon>
  )
}

export function QueueIcon({ className }: IconProps) {
  return (
    <Icon className={className}>
      <rect x="5" y="4" width="14" height="17" rx="2" />
      <path d="M9 3h6v3H9zM9 11h6M9 15h4" />
    </Icon>
  )
}

export function SessionsIcon({ className }: IconProps) {
  return (
    <Icon className={className}>
      <path d="M4 10h16M6 10v9M18 10v9M8 10V6M16 10V6" />
      <circle cx="12" cy="4" r="1.5" />
    </Icon>
  )
}

export function CreateOrderIcon({ className }: IconProps) {
  return (
    <Icon className={className}>
      <path d="M8 4h8l1 3H7l1-3zM5 7h14v13H5zM12 11v5M9.5 13.5h5" />
    </Icon>
  )
}

export function SearchIcon({ className }: IconProps) {
  return (
    <Icon className={className}>
      <circle cx="11" cy="11" r="6" />
      <path d="m20 20-4.5-4.5" />
    </Icon>
  )
}

/** An "i" in a circle: the branch info page (H2). */
export function InfoIcon({ className }: IconProps) {
  return (
    <Icon className={className}>
      <circle cx="12" cy="12" r="9" />
      <path d="M12 11v5M12 8h.01" />
    </Icon>
  )
}

/** A bell: the notifications entry in the top bar (H3). */
export function BellIcon({ className }: IconProps) {
  return (
    <Icon className={className}>
      <path d="M6 16V11a6 6 0 0 1 12 0v5l1.5 2h-15z" />
      <path d="M10 20a2 2 0 0 0 4 0" />
    </Icon>
  )
}

/** A scroll of lines: the system log entry (H4). */
export function LogIcon({ className }: IconProps) {
  return (
    <Icon className={className}>
      <path d="M6 3h12v18H6z" />
      <path d="M9 8h6" />
      <path d="M9 12h6" />
      <path d="M9 16h4" />
    </Icon>
  )
}

/** Three bars: the statistics page (H5). */
export function ChartIcon({ className }: IconProps) {
  return (
    <Icon className={className}>
      <path d="M4 20h16" />
      <path d="M7 16v-5" />
      <path d="M12 16V6" />
      <path d="M17 16v-8" />
    </Icon>
  )
}

/** A broom: the data cleanup page (H7). */
export function BroomIcon({ className }: IconProps) {
  return (
    <Icon className={className}>
      <path d="M19 3l-8 8" />
      <path d="M11 11l-6 6a3 3 0 0 0 4 4l6-6" />
      <path d="M9 13l4 4" />
    </Icon>
  )
}

/** A clock with a back arrow: the audit log (I2). */
export function HistoryIcon({ className }: IconProps) {
  return (
    <Icon className={className}>
      <path d="M3 12a9 9 0 1 0 3-6.7" />
      <path d="M3 4v5h5" />
      <path d="M12 8v4l3 2" />
    </Icon>
  )
}

/** A speech bubble: feedback (I3). */
export function FeedbackIcon({ className }: IconProps) {
  return (
    <Icon className={className}>
      <path d="M4 5h16v11H9l-5 4z" />
      <path d="M8 9h8" />
      <path d="M8 12h5" />
    </Icon>
  )
}

export function EyeIcon({ className }: IconProps) {
  return (
    <Icon className={className}>
      <path d="M2 12s3.5-7 10-7 10 7 10 7-3.5 7-10 7S2 12 2 12Z" />
      <circle cx="12" cy="12" r="3" />
    </Icon>
  )
}

export function EyeOffIcon({ className }: IconProps) {
  return (
    <Icon className={className}>
      <path d="M2 12s3.5-7 10-7c1.6 0 3 .4 4.3 1M22 12s-3.5 7-10 7c-1.6 0-3-.4-4.3-1" />
      <path d="M9.9 9.9a3 3 0 0 0 4.2 4.2" />
      <path d="m3 3 18 18" />
    </Icon>
  )
}

export function GlobeIcon({ className }: IconProps) {
  return (
    <Icon className={className}>
      <circle cx="12" cy="12" r="9" />
      <path d="M3 12h18" />
      <path d="M12 3a13.5 13.5 0 0 1 0 18a13.5 13.5 0 0 1 0-18z" />
    </Icon>
  )
}

export function SunIcon({ className }: IconProps) {
  return (
    <Icon className={className}>
      <circle cx="12" cy="12" r="4" />
      <path d="M12 2v2M12 20v2M2 12h2M20 12h2M4.9 4.9l1.4 1.4M17.7 17.7l1.4 1.4M4.9 19.1l1.4-1.4M17.7 6.3l1.4-1.4" />
    </Icon>
  )
}

export function MoonIcon({ className }: IconProps) {
  return (
    <Icon className={className}>
      <path d="M21 12.8A9 9 0 1 1 11.2 3a7 7 0 0 0 9.8 9.8z" />
    </Icon>
  )
}

export function ReceiptIcon({ className }: IconProps) {
  return (
    <Icon className={className}>
      <path d="M5 3h14v18l-2.3-1.5L14 21l-2-1.5L10 21l-2.7-1.5L5 21z" />
      <path d="M9 8h6M9 12h6M9 16h4" />
    </Icon>
  )
}
